<template>
  <div class="mention-review-media-player">
    <QBtn
      v-if="!hideClose"
      align="between"
      class="float-right"
      flat
      icon="cancel"
      rounded
      @click="closePlayer"
    >
      Close
    </QBtn>
    <div
      v-if="!clip"
      class="q-pa-sm q-ma-sm"
    >
      There are no clips available for that channel around that time
    </div>
    <div v-else-if="currentClip">
      <div v-if="type === 'radio'">
        <audio
          ref="mediaPlayer"
          autoplay
          controls
          :src="currentClip.media_url"
          style="width: 480px"
          @ended="ended"
        >
          Your browser does not support the <code>audio</code> element.
        </audio>
      </div>
      <div v-else>
        <video
          ref="mediaPlayer"
          autoplay
          controls
          width="500"
          @ended="ended"
          @mouseout="hideControls"
          @mouseover="showControls"
        >
          <source
            :src="currentClip.media_url"
            type="video/mp4"
          />
          Your browser does not support HTML5 video.
        </video>
      </div>
    </div>
    <div class="controls">
      <QBtn
        v-if="currentClip && currentClip.previous"
        align="left"
        class="q-m-md"
        flat
        icon="skip_previous"
        @click="playClipById(currentClip.previous.id)"
      />
      <QBtn
        v-if="currentClip && currentClip.next"
        align="left"
        class="q-m-md"
        flat
        icon="skip_next"
        @click="playClipById(currentClip.next.id)"
      />

      <span
        v-if="currentClip"
        style="padding: 0 10px"
      >
        {{
          formatIntlDate(currentTime, {
            dateStyle: "short",
            timeStyle: "long",
            timeZone: currentClip.source.time_zone,
          })
        }}
      </span>
      <QSelect
        v-model="playbackRate"
        class="float-right"
        dense
        :label="`x ${playbackRate}`"
        :options="playbackRates"
        outlined
        style="width: 60px; display: inline-block; margin-top: -5px"
      />
      <span v-if="syndications.length === 1">
        {{ clip.source.name }} {{ clip.source.location }}
      </span>
      <QSelect
        v-if="syndications.length > 1"
        v-model="selectedSyndication"
        class="float-right"
        dense
        label="Syndications"
        option-label="name"
        option-value="id"
        :options="syndications"
        outlined
        style="display: inline-block; margin-top: -5px"
        @update:model-value="$emit('syndication-updated', selectedSyndication)"
      />
    </div>
  </div>
</template>

<script>
import { formatIntlDate } from "shared/helpers/date";

export default {
  name: "MentionReviewMediaPlayer",
  props: {
    clip: {
      type: Object,
      default: null,
    },
    syndications: {
      type: Array,
      default: () => [],
    },
    originalSourceId: {
      type: [Number, String],
      default: null,
    },
    type: {
      type: String,
      required: true,
    },
    hideClose: {
      type: Boolean,
    },
  },
  emits: ["syndication-updated", "toggle-player"],
  data() {
    return {
      selectedSyndication: null,
      currentClip: undefined,
      player: undefined,
      playbackRate: 1,
      playbackRates: [0.5, 1, 1.5, 2, 4],
      playing: false,
      currentTime: 0,
      polling: null,
    };
  },
  computed: {
    hasNextClip() {
      return "next" in this.currentClip;
    },
  },
  watch: {
    playbackRate() {
      this.player.playbackRate = this.playbackRate;
    },
    clip() {
      this.playClipById(this.clip?.id);
    },
  },
  beforeMount() {
    this.currentClip = this.clip;

    if (this.clip) this.currentTime = this.clip.start_time;
  },
  mounted() {
    this.player = this.$refs.mediaPlayer;

    if (this.clip) {
      this.player.onplaying = this.startPollingClipTime;
      this.player.onpause = this.clearPolling;
      this.player.onseeked = this.updateCurrentTime;
    }

    this.updateSyndication();
  },
  methods: {
    formatIntlDate,
    updateSyndication() {
      const source = this.currentClip
        ? this.currentClip.source.id
        : this.originalSourceId;

      this.selectedSyndication = this.syndications.find(
        ({ sourceId }) => sourceId === source
      );
    },
    closePlayer() {
      this.$emit("toggle-player");
    },
    async playClipById(id) {
      this.player = this.$refs.mediaPlayer;

      try {
        if (!id) {
          this.currentClip = null;

          return;
        }

        this.currentClip = (
          await this.$streemApiAdmin.get(`${this.type}_clips/${id}`)
        ).data;

        if (!this.currentClip) return;

        this.updateSyndication();
        this.playClip();
      } catch {
        this.player.pause();

        this.$q.notify({
          message: `Error getting ${this.type} clip id: ${id}`,
          color: "warning",
          position: "center",
          closeBtn: "Dismiss",
        });
      }
    },
    playClip() {
      this.player.load();
      this.player.playbackRate = this.playbackRate;
      this.playing = true;
      this.player.play();
    },
    startPollingClipTime() {
      if (this.polling) {
        clearInterval(this.polling);
      }

      this.polling = setInterval(() => {
        this.updateCurrentTime();
      }, 200);
    },
    clearPolling() {
      clearInterval(this.polling);
    },
    updateCurrentTime() {
      this.currentTime =
        this.currentClip.start_time + Math.round(this.player.currentTime);
    },
    ended() {
      if (this.hasNextClip) {
        this.playClipById(this.currentClip.next.id);
      }
    },
    hideControls() {
      this.$refs.mediaPlayer.controls = false;
    },
    showControls() {
      this.$refs.mediaPlayer.controls = true;
    },
  },
};
</script>

<style lang="scss" scoped>
.mention-review-media-player {
  position: fixed;
  z-index: 9999;
  top: 5px;
  right: 25%;
  box-shadow: 4px 7px 8px 4px #ccc;
  background: #fff;
}

.controls {
  background-color: #f5f5f5;
  padding: 5px;
  margin-top: -5px;
}
</style>
